import api from "@/config/axiosConfig";
import {
  BillingAmountResponse,
  BillingDetailsResponse,
  CheckoutRequest,
  CheckoutResponse,
  CreateBillingDetailsRequest,
  UpdateBillingDetailsRequest,
  VerifyPaymentRequest,
  VerifyPaymentResponse,
} from "@/types/billing";
import {
  CreateDatabaseRequest,
  CreateDatabaseResponse,
  DatabaseListResponse,
  DatabaseResponse,
} from "@/types/database";
import {
  AddCaseConversationRequest,
  CaseConversationListResponse,
  CreateSupportCaseRequest,
  SupportCaseListResponse,
  SupportCaseResponse,
  SupportCaseStatsResponse,
  SupportCaseWithConversationsResponse,
  UpdateSupportCaseRequest,
} from "@/types/support-case";
import { AxiosResponse } from "axios";

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  details?: any;
}

// Generic error handler for API calls
const handleApiError = (error: any): ApiResponse => {
  // The error message is already sanitized by axios interceptor
  const errorMessage =
    error.message || "Something went wrong. Please try again.";

  return {
    success: false,
    error: errorMessage,
    details: error.response?.data?.details || null,
  };
};

export const databaseApi = {
  // Create database
  async createDatabase(
    data: CreateDatabaseRequest
  ): Promise<ApiResponse<CreateDatabaseResponse>> {
    try {
      const response: AxiosResponse<CreateDatabaseResponse> = await api.post(
        "/databases",
        data
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  async createShadowDatabase(
    dbname: string
  ): Promise<ApiResponse<CreateDatabaseResponse>> {
    try {
      const response: AxiosResponse<CreateDatabaseResponse> = await api.post(
        `/databases/shadowdatabase/${dbname}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get all databases
  async getAllDatabases(): Promise<ApiResponse<DatabaseListResponse>> {
    try {
      const response: AxiosResponse<DatabaseListResponse> = await api.get(
        "/databases"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get database by name
  async getDatabaseByName(
    dbname: string
  ): Promise<ApiResponse<DatabaseResponse>> {
    try {
      const response: AxiosResponse<DatabaseResponse> = await api.get(
        `/databases/${dbname}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Delete database
  async deleteDatabase(
    dbname: string
  ): Promise<ApiResponse<{ message: string }>> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.delete(
        `/databases/${dbname}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Undo delete
  async undoDelete(dbname: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.post(
        `/databases/${dbname}/undo-delete`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Force delete
  async forceDelete(dbname: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.delete(
        `/databases/${dbname}/force`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get deleted databases
  async getDeletedDatabases(): Promise<ApiResponse<DatabaseListResponse>> {
    try {
      const response: AxiosResponse<DatabaseListResponse> = await api.get(
        "/databases/audit/deleted"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Regenerate passwords
  async regeneratePasswords(
    dbname: string
  ): Promise<ApiResponse<{ users: any; message: string }>> {
    try {
      const response: AxiosResponse<{ users: any; message: string }> =
        await api.post(`/databases/${dbname}/regenerate-password`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get connection strings
  async getConnectionStrings(dbname: string): Promise<
    ApiResponse<{
      connectionStrings: any;
      shadowDbConnectionStrings: any;
      message: string;
    }>
  > {
    try {
      const response: AxiosResponse<{
        connectionStrings: any;
        shadowDbConnectionStrings: any;
        message: string;
      }> = await api.get(`/databases/${dbname}/connection-strings`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get database usage
  async getDatabaseUsage(
    dbname: string
  ): Promise<ApiResponse<{ usage: any; message: string }>> {
    try {
      const response: AxiosResponse<{ usage: any; message: string }> =
        await api.get(`/databases/${dbname}/usage`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Update database capacity
  async updateDatabaseCapacity(
    dbname: string,
    size: string
  ): Promise<ApiResponse<{ message: string; database: any }>> {
    try {
      const response: AxiosResponse<{ message: string; database: any }> =
        await api.put(`/databases/${dbname}/capacity`, { size });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Update database tags
  async updateDatabaseTags(
    dbname: string,
    tags: string[]
  ): Promise<ApiResponse<{ message: string; database: any }>> {
    try {
      const response: AxiosResponse<{ message: string; database: any }> =
        await api.put(`/databases/${dbname}/tags`, { tags });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get user quota information
  async getQuotaInfo(): Promise<ApiResponse<{ quotaInfo: any }>> {
    try {
      const response: AxiosResponse<{ quotaInfo: any }> = await api.get(
        "/databases/quota"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get user quota statistics
  async getQuotaStats(): Promise<ApiResponse<{ stats: any }>> {
    try {
      const response: AxiosResponse<{ stats: any }> = await api.get(
        "/databases/quota/stats"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get connection statistics
  async getConnectionStats(
    dbname: string
  ): Promise<ApiResponse<{ stats: any; message: string }>> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        data: { stats: any; message: string };
      }> = await api.get(`/databases/${dbname}/connections`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Close all connections
  async closeAllConnections(
    dbname: string
  ): Promise<ApiResponse<{ closed: number; message: string }>> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        data: { closed: number; message: string };
      }> = await api.post(`/databases/${dbname}/connections/close`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Cleanup idle connections
  async cleanupIdleConnections(
    dbname: string,
    idleTimeoutSeconds?: number
  ): Promise<ApiResponse<{ cleaned: number; message: string }>> {
    try {
      const url = `/databases/${dbname}/connections/cleanup${
        idleTimeoutSeconds ? `?idleTimeoutSeconds=${idleTimeoutSeconds}` : ""
      }`;
      const response: AxiosResponse<{
        success: boolean;
        data: { cleaned: number; message: string };
      }> = await api.post(url);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },
};

export const supportCaseApi = {
  // Create support case
  async createSupportCase(
    data: CreateSupportCaseRequest
  ): Promise<ApiResponse<SupportCaseResponse>> {
    try {
      const response: AxiosResponse<SupportCaseResponse> = await api.post(
        "/support-cases",
        data
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get user's support cases
  async getUserSupportCases(
    includeClosed: boolean = false
  ): Promise<ApiResponse<SupportCaseListResponse>> {
    try {
      const response: AxiosResponse<SupportCaseListResponse> = await api.get(
        `/support-cases?includeClosed=${includeClosed}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get support case by ID
  async getSupportCaseById(
    caseId: string
  ): Promise<ApiResponse<SupportCaseResponse>> {
    try {
      const response: AxiosResponse<SupportCaseResponse> = await api.get(
        `/support-cases/${caseId}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get support case with conversations
  async getSupportCaseWithConversations(
    caseId: string
  ): Promise<ApiResponse<SupportCaseWithConversationsResponse>> {
    try {
      const response: AxiosResponse<SupportCaseWithConversationsResponse> =
        await api.get(`/support-cases/${caseId}/with-conversations`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Update support case
  async updateSupportCase(
    caseId: string,
    data: UpdateSupportCaseRequest
  ): Promise<ApiResponse<SupportCaseResponse>> {
    try {
      const response: AxiosResponse<SupportCaseResponse> = await api.put(
        `/support-cases/${caseId}`,
        data
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Delete support case
  async deleteSupportCase(
    caseId: string
  ): Promise<ApiResponse<{ message: string }>> {
    try {
      const response: AxiosResponse<{ message: string }> = await api.delete(
        `/support-cases/${caseId}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Add conversation message
  async addCaseConversation(
    caseId: string,
    data: AddCaseConversationRequest
  ): Promise<ApiResponse<CaseConversationListResponse>> {
    try {
      const response: AxiosResponse<CaseConversationListResponse> =
        await api.post(`/support-cases/${caseId}/conversations`, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get case conversations
  async getCaseConversations(
    caseId: string
  ): Promise<ApiResponse<CaseConversationListResponse>> {
    try {
      const response: AxiosResponse<CaseConversationListResponse> =
        await api.get(`/support-cases/${caseId}/conversations`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Get support case statistics
  async getSupportCaseStats(): Promise<ApiResponse<SupportCaseStatsResponse>> {
    try {
      const response: AxiosResponse<SupportCaseStatsResponse> = await api.get(
        "/support-cases/stats"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },
};

export const billingApi = {
  // Get billing details
  async getBillingDetails(): Promise<ApiResponse<BillingDetailsResponse>> {
    try {
      const response: AxiosResponse<BillingDetailsResponse> = await api.get(
        "/billing"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Create or update billing details
  async upsertBillingDetails(
    data: CreateBillingDetailsRequest
  ): Promise<ApiResponse<BillingDetailsResponse>> {
    try {
      const response: AxiosResponse<BillingDetailsResponse> = await api.post(
        "/billing",
        data
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Update billing details
  async updateBillingDetails(
    data: UpdateBillingDetailsRequest
  ): Promise<ApiResponse<BillingDetailsResponse>> {
    try {
      const response: AxiosResponse<BillingDetailsResponse> = await api.put(
        "/billing",
        data
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Update billing details
  async getBillAmount(): Promise<ApiResponse<BillingAmountResponse>> {
    try {
      const response: AxiosResponse<BillingAmountResponse> = await api.get(
        `/billing/amount`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Checkout
  async checkout(
    data: CheckoutRequest
  ): Promise<ApiResponse<CheckoutResponse>> {
    try {
      const response: AxiosResponse<CheckoutResponse> = await api.post(
        "/billing/checkout",
        data
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Verify payment
  async verifyPayment(
    data: VerifyPaymentRequest
  ): Promise<ApiResponse<VerifyPaymentResponse>> {
    try {
      const response: AxiosResponse<VerifyPaymentResponse> = await api.post(
        "/billing/verify",
        data
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Activate billing
  async activateBilling(): Promise<ApiResponse<BillingDetailsResponse>> {
    try {
      const response: AxiosResponse<BillingDetailsResponse> = await api.post(
        "/billing/activate"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },

  // Deactivate billing
  async deactivateBilling(): Promise<ApiResponse<BillingDetailsResponse>> {
    try {
      const response: AxiosResponse<BillingDetailsResponse> = await api.post(
        "/billing/deactivate"
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      return handleApiError(error);
    }
  },
};
